import { useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { put } from "@common/api/apiShared";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { IEntityRiskAreasDialog } from "./EntityRiskAreasDialog";
import { EntityRiskArea } from "./EntityRiskAreasDialog.types";

export function useEntityRiskAreasDialog(props: IEntityRiskAreasDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();

    const url = `/entities/${props.entityType}/${props.entityId}/risk-areas`;
    const { isLoading, data } = useDotLegalQuery<Array<EntityRiskArea>>(url);

    // null until the user changes something — the query result is the source of truth for the initial
    // selection, so we derive from it rather than mirroring into state on load.
    const [editedSelectedIds, setEditedSelectedIds] = useState<Array<string> | null>(null);
    // Removed areas the user opted to also delete the risk assessments for (default: keep for history).
    const [deleteAssessmentIds, setDeleteAssessmentIds] = useState<Array<string>>([]);

    // The links that already exist in the backend. Unchecking one of these removes a persisted link on
    // save, so those are surfaced as a warning inside the dialog.
    const persistedSelectedIds = (data ?? []).filter((riskArea) => riskArea.isSelected).map((riskArea) => riskArea.riskAreaId);
    const selectedIds = editedSelectedIds ?? persistedSelectedIds;

    const isBeingRemoved = (riskAreaId: string) => persistedSelectedIds.includes(riskAreaId) && !selectedIds.includes(riskAreaId);

    const rows = (data ?? []).map((riskArea) => ({
        riskAreaId: riskArea.riskAreaId,
        name: riskArea.name,
        isSelected: selectedIds.includes(riskArea.riskAreaId),
        // The delete-assessments choice only applies to an existing link that is being removed.
        isBeingRemoved: isBeingRemoved(riskArea.riskAreaId),
        deleteAssessments: deleteAssessmentIds.includes(riskArea.riskAreaId),
    }));

    const removedRiskAreaNames = rows.filter((row) => row.isBeingRemoved).map((row) => row.name);

    const hasRemovals = removedRiskAreaNames.length > 0;

    const toggleRiskArea = (riskAreaId: string) => {
        const isCurrentlySelected = selectedIds.includes(riskAreaId);
        setEditedSelectedIds(isCurrentlySelected ? selectedIds.filter((id) => id !== riskAreaId) : [...selectedIds, riskAreaId]);
        // Re-selecting a previously removed area clears its (now irrelevant) delete-assessments choice.
        if (!isCurrentlySelected) setDeleteAssessmentIds((current) => current.filter((id) => id !== riskAreaId));
    };

    const toggleDeleteAssessments = (riskAreaId: string) => {
        setDeleteAssessmentIds((current) => (current.includes(riskAreaId) ? current.filter((id) => id !== riskAreaId) : [...current, riskAreaId]));
    };

    const saveMutation = useDotLegalMutation(
        () => put(url, { riskAreaIds: selectedIds, riskAreaIdsToDeleteAssessments: deleteAssessmentIds.filter(isBeingRemoved) }),
        {
            onSuccess: () => {
                snackbar.show(translateString("riskAreasUpdated"));
                props.onSaved?.();
                props.onClose();
            },
        }
    );

    return {
        isLoading,
        rows,
        hasRiskAreas: (data ?? []).length > 0,
        hasRemovals,
        toggleRiskArea,
        toggleDeleteAssessments,
        removedRiskAreaNames,
        onSaveClick: () => saveMutation.mutateAsync(),
        isSaving: saveMutation.isLoading,
    };
}
